import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormattedMessage } from '../../i18n/FormattedMessage';

/**
 * AriesFilterGroup — one standardized section inside AriesFilterPanel: a label
 * row (left) with an optional "Select all | Clear all" action group (right,
 * for multi-select groups), followed by either a scrollable checkbox list
 * (driven by `options`/`values`) or custom `children` (e.g. a single-select
 * radio group or month/year selects).
 *
 * Reference layout: Dashboard "Category" filter group.
 */
const AriesFilterGroup = ({
  label,
  options,
  values,
  onToggle,
  onSelectAll,
  onClearAll,
  showSelectAll,
  children,
}) => {
  const intl = useIntl();
  return (
    <div className="aries-filter-group">
      <div className="aries-filter-group-head">
        <span className="aries-filter-group-label">
          {intl.formatMessage({ id: label, defaultMessage: label })}
        </span>
        {showSelectAll && (
          <span className="aries-filter-group-actions">
            <button
              type="button"
              className="aries-link aries-filter-group-action"
              onClick={onSelectAll}
            >
              <FormattedMessage id="Select all" defaultMessage="Select all" />
            </button>
            <span className="aries-muted">|</span>
            <button
              type="button"
              className="aries-link aries-filter-group-action"
              onClick={onClearAll}
            >
              <FormattedMessage id="Clear all" defaultMessage="Clear all" />
            </button>
          </span>
        )}
      </div>
      {children || (
        <div className="aries-filter-group-list">
          {options.map((name) => (
            <label key={name} className="aries-filter-group-option">
              <input
                type="checkbox"
                checked={!!values[name]}
                onChange={() => onToggle(name)}
              />
              <FormattedMessage id={name} defaultMessage={name} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

AriesFilterGroup.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array,
  values: PropTypes.object,
  onToggle: PropTypes.func,
  onSelectAll: PropTypes.func,
  onClearAll: PropTypes.func,
  showSelectAll: PropTypes.bool,
  children: PropTypes.node,
};

AriesFilterGroup.defaultProps = {
  options: [],
  values: {},
  onToggle: () => {},
  onSelectAll: () => {},
  onClearAll: () => {},
  showSelectAll: false,
  children: null,
};

export default AriesFilterGroup;
